import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { store } from "../Pages/ReduxStore/store";

// ===================================
// MAIN USE CASES
// ===================================

// 1. TIMEZONE TO UTC
// Convert a time from any timezone to UTC
// Example: User in New York enters 2:30 PM, convert to UTC
// const result = toUTC('2026-03-20 14:30:00', 'America/New_York');
// result.utcString => "2026-03-20 18:30:00"

// 2. UTC TO TIMEZONE
// Convert a UTC time to any timezone
// Example: UTC 6:00 PM, show as IST (Indian Standard Time)
// const result = fromUTC('2026-03-20 18:00:00', 'Asia/Kolkata');
// result.tzString => "2026-03-20 23:30:00"

// 3. TIMEZONE TO TIMEZONE
// Convert between any two timezones
// Example: New York time to Tokyo time
// const result = convertTimeZone('2026-03-20 14:30:00', 'America/New_York', 'Asia/Tokyo');
// result.tzString => "2026-03-21 02:30:00"

// 4. GET CURRENT TIME
// Get current time in UTC or specific timezone
// const utcNow = getCurrentUTC();
// const istNow = getCurrentInTimeZone('Asia/Kolkata');

// 5. FORMAT DATES
// Format dates in various ways
// const formatted = formatDate('2026-03-20 14:30:00', 'DD/MM/YYYY HH:mm');
// formatted => "20/03/2026 14:30"

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Convert a date string in a specific timezone to UTC
 * @param {string} dateTimeString - The date-time string (e.g., "2026-03-20 14:30:00")
 * @param {string} timeZone - The timezone string (e.g., "America/New_York", "Europe/London", "Asia/Kolkata")
 * @returns {object} Object containing both UTC string and dayjs object
 */
export const toUTC = (dateTimeString) => {
  const timeZone = store.getState().auth.loginInfo.timeZone || dayjs.tz.guess(); // Get user's timezone from store or guess it
  //const timeZone = "Asia/Kuala_Lumpur"; // Default to New York if not set
  try {
    if (!dateTimeString || !timeZone) {
      throw new Error("Both dateTimeString and timeZone are required");
    }

    const dateInTimeZone = dayjs.tz(dateTimeString, timeZone);

    if (!dateInTimeZone.isValid()) {
      throw new Error("Invalid date format or timezone");
    }

    const utcDate = dateInTimeZone.utc();

    return {
      utcString: utcDate.format("DD-MM-YYYY HH:mm:ss"),
      utcISOString: utcDate.toISOString(),
      utcUnix: utcDate.unix(),
      dayjsObject: utcDate,
    };
  } catch (error) {
    console.error("Error converting to UTC:", error);
    throw error;
  }
};

/**
 * Convert a UTC date string to a specific timezone
 * @param {string} utcDateString - The UTC date-time string (e.g., "2026-03-20 14:30:00")
 * @param {string} targetTimeZone - The target timezone string (e.g., "America/New_York", "Europe/London", "Asia/Kolkata")
 * @returns {object} Object containing both timezone string and dayjs object
 */
export const fromUTC = (utcDateString) => {
  const targetTimeZone =
    store.getState().auth.loginInfo.timeZone || dayjs.tz.guess(); // Get user's timezone from store or guess it
  //const targetTimeZone = "Asia/Kuala_Lumpur"; // Default to New York if not set
  console.log("fromUTC called with:", { utcDateString, targetTimeZone });
  try {
    if (!utcDateString || !targetTimeZone) {
      throw new Error("Both utcDateString and targetTimeZone are required");
    }

    // Parse as UTC
    const utcDate = dayjs.utc(utcDateString);

    if (!utcDate.isValid()) {
      throw new Error("Invalid UTC date format");
    }

    // Convert to target timezone
    const dateInTimeZone = utcDate.tz(targetTimeZone);

    return {
      tzString: dateInTimeZone.format("DD-MM-YYYY HH:mm:ss"),
      tzISOString: dateInTimeZone.toISOString(),
      tzUnix: dateInTimeZone.unix(),
      tzOffset: dateInTimeZone.format("Z"),
      dayjsObject: dateInTimeZone,
    };
  } catch (error) {
    console.error("Error converting from UTC:", error);
    throw error;
  }
};

/**
 * Convert from one timezone to another (both non-UTC)
 * @param {string} dateTimeString - The date-time string
 * @param {string} fromTimeZone - The source timezone
 * @param {string} toTimeZone - The target timezone
 * @returns {object} Object containing the converted date in target timezone
 */
export const convertTimeZone = (dateTimeString, fromTimeZone, toTimeZone) => {
  try {
    if (!dateTimeString || !fromTimeZone || !toTimeZone) {
      throw new Error(
        "dateTimeString, fromTimeZone, and toTimeZone are all required",
      );
    }

    // Parse in source timezone, convert to target timezone
    const sourceDate = dayjs.tz(dateTimeString, fromTimeZone);

    if (!sourceDate.isValid()) {
      throw new Error("Invalid date format or source timezone");
    }

    const targetDate = sourceDate.tz(toTimeZone);

    return {
      tzString: targetDate.format("DD-MM-YYYY HH:mm:ss"),
      tzISOString: targetDate.toISOString(),
      tzUnix: targetDate.unix(),
      tzOffset: targetDate.format("Z"),
      fromTimeZone,
      toTimeZone,
      dayjsObject: targetDate,
    };
  } catch (error) {
    console.error("Error converting timezone:", error);
    throw error;
  }
};

/**
 * Get current time in UTC
 * @returns {object} Current time in UTC
 */
export const getCurrentUTC = () => {
  const now = dayjs.utc();

  return {
    utcString: now.format("DD-MM-YYYY HH:mm:ss"),
    utcISOString: now.toISOString(),
    utcUnix: now.unix(),
    dayjsObject: now,
  };
};

/**
 * Get current time in a specific timezone
 * @param {string} timeZone - The timezone string
 * @returns {object} Current time in the specified timezone
 */
export const getCurrentInTimeZone = () => {
  const timeZone = store.getState().auth.loginInfo.timeZone || dayjs.tz.guess(); // Get user's timezone from store or guess it
  //const timeZone = "Asia/Kuala_Lumpur"; // Default to New York if not set
  try {
    if (!timeZone) {
      throw new Error("timeZone is required");
    }

    const now = dayjs().tz(timeZone);

    return {
      tzString: now.format("DD-MM-YYYY HH:mm:ss"),
      tzISOString: now.toISOString(),
      tzUnix: now.unix(),
      tzOffset: now.format("Z"),
      timeZone,
      dayjsObject: now,
    };
  } catch (error) {
    console.error("Error getting current time in timezone:", error);
    throw error;
  }
};

/**
 * Format a date string with custom format
 * @param {string} dateString - The date-time string
 * @param {string} format - The desired format (default: 'DD-MM-YYYY HH:mm:ss')
 * @param {string} timeZone - Optional timezone for parsing
 * @returns {string} Formatted date string
 */
export const formatDate = (
  dateString,
  format = "DD-MM-YYYY HH:mm:ss",
  timeZone = null,
) => {
  try {
    let date;

    if (timeZone) {
      date = dayjs.tz(dateString, timeZone);
    } else {
      date = dayjs(dateString);
    }

    if (!date.isValid()) {
      throw new Error("Invalid date format");
    }

    return date.format(format);
  } catch (error) {
    console.error("Error formatting date:", error);
    throw error;
  }
};

/**
 * Get offset between two timezones in hours
 * @param {string} fromTimeZone - Source timezone
 * @param {string} toTimeZone - Target timezone
 * @returns {number} Offset in hours
 */
export const getTimeZoneOffset = (fromTimeZone, toTimeZone) => {
  try {
    const now = dayjs().tz(fromTimeZone);
    const from = now.utcOffset();
    const to = dayjs().tz(toTimeZone).utcOffset();

    return (to - from) / 60;
  } catch (error) {
    console.error("Error getting timezone offset:", error);
    throw error;
  }
};

/**
 * Get list of all available timezones
 * @returns {array} Array of timezone strings
 */
export const getAllTimeZones = () => {
  return dayjs.tz.names();
};

/**
 * Get date without converting utc
 * @param {string|dayjs.Dayjs} value - The date value to convert
 * @returns {dayjs.Dayjs|null} Dayjs object in store timezone or null
 */
export const getDateWithOutUTC = (value) => {
  const timezone = store.getState().auth.loginInfo.timeZone || dayjs.tz.guess(); // Get user's timezone from store or guess it
  //const timezone = "Asia/Kuala_Lumpur"; // Default to New York if not set
  if (!value) return null;

  return dayjs(value).tz(timezone, true);
};

/**
 * Get current year in user's timezone
 * @param {string} timeZone - Optional timezone (defaults to user's timezone from store)
 * @returns {number} Current year in the specified timezone
 */
export const getCurrentYearInTimeZone = (timeZone = null) => {
  try {
    const userTimeZone =
      timeZone || store.getState().auth.loginInfo.timeZone || dayjs.tz.guess();
    return dayjs().tz(userTimeZone).year();
  } catch (error) {
    console.error("Error getting current year in timezone:", error);
    return dayjs().year(); // Fallback to local year
  }
};

export const toUTCWithStartOf = (value) => {
  try {
    if (!value) throw new Error("Value is required");

    const utcDate = dayjs(value)
      .startOf("day")
      .utc(true); // ✅ KEY FIX

    return {
      utcString: utcDate.format("DD-MM-YYYY HH:mm:ss"),
      utcISOString: utcDate.toISOString(), // ✅ 2026-04-15T00:00:00Z
      utcUnix: utcDate.unix(),
      dayjsObject: utcDate,
    };
  } catch (error) {
    console.error("Error converting to UTC:", error);
    throw error;
  }
};

export default {
  toUTC,
  toUTCWithStartOf,
  fromUTC,
  convertTimeZone,
  getCurrentUTC,
  getCurrentInTimeZone,
  formatDate,
  getTimeZoneOffset,
  getAllTimeZones,
  getDateWithOutUTC,
  getCurrentYearInTimeZone,
};
